import React, { useEffect, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, siteImages } from '../data/mock';
import { Phone, Mail, MapPin, Clock, Send, Building2, ArrowRight } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    document.title = 'Contact Clarity Lens Co | Customer Support';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        let detail = 'Unable to send your message right now. Please try again later.';
        try {
          const data = await res.json();
          if (data && data.detail) detail = typeof data.detail === 'string' ? data.detail : detail;
        } catch (_) { /* ignore */ }
        throw new Error(detail);
      }
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />
      <CartDrawer />

      <main className="pt-[116px] lg:pt-[136px]">
        {/* Editorial Banner */}
        <section className="relative">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10 lg:pt-16 pb-10 lg:pb-16">
            <div className="grid lg:grid-cols-12 gap-8 items-end">
              <div className="lg:col-span-7">
                <span className="text-eyebrow text-copper-600 font-mono">— Customer Support</span>
                <h1 className="font-display text-ink-900 text-display-2xl mt-4">
                  Let&rsquo;s <span className="italic font-light">talk.</span>
                </h1>
                <p className="mt-5 text-ink-500 text-lg max-w-xl">
                  Questions about fit, lenses, shipping or an order? We&rsquo;re here and happy to help.
                </p>
              </div>
              <div className="lg:col-span-5">
                <div className="aspect-[16/9] lg:aspect-[5/6] overflow-hidden bg-cream-100">
                  <img src={siteImages.contactBanner} alt="Contact Clarity Lens Co" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pb-20 lg:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Info column */}
            <div className="lg:col-span-5 space-y-8">
              {/* Corporate card */}
              <div className="bg-ink-900 text-cream-50 p-8 relative overflow-hidden">
                <div className="flex items-center gap-3 mb-5">
                  <Building2 className="w-5 h-5 text-copper-400" />
                  <span className="text-eyebrow text-copper-400 font-mono">Our Company</span>
                </div>
                <p className="font-display text-3xl leading-tight">{CONTACT_INFO.corpName}</p>
                <p className="text-cream-200/70 mt-4 text-sm">
                  {CONTACT_INFO.address.street}<br />
                  {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}<br />
                  {CONTACT_INFO.address.country}
                </p>
              </div>

              {/* Contact rows */}
              <div className="border-y border-ink-200">
                <a
                  href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                  className="flex items-center justify-between py-6 border-b border-ink-200 group"
                >
                  <div className="flex items-center gap-4">
                    <Phone className="w-5 h-5 text-copper-600" strokeWidth={1.2} />
                    <div>
                      <p className="text-[10px] tracking-[0.22em] uppercase text-ink-400 font-mono">Phone</p>
                      <p className="font-display text-xl text-ink-900">{CONTACT_INFO.phone}</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-ink-400 group-hover:translate-x-1 group-hover:text-copper-600 transition-all" />
                </a>
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="flex items-center justify-between py-6 border-b border-ink-200 group"
                >
                  <div className="flex items-center gap-4">
                    <Mail className="w-5 h-5 text-copper-600" strokeWidth={1.2} />
                    <div>
                      <p className="text-[10px] tracking-[0.22em] uppercase text-ink-400 font-mono">Email</p>
                      <p className="font-display text-lg text-ink-900">{CONTACT_INFO.email}</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-ink-400 group-hover:translate-x-1 group-hover:text-copper-600 transition-all" />
                </a>
                <div className="flex items-start py-6 border-b border-ink-200">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 text-copper-600 mt-0.5" strokeWidth={1.2} />
                    <div>
                      <p className="text-[10px] tracking-[0.22em] uppercase text-ink-400 font-mono">Address</p>
                      <p className="font-display text-lg text-ink-900 leading-snug mt-0.5">
                        {CONTACT_INFO.address.street}<br />
                        {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}<br />
                        {CONTACT_INFO.address.country}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center py-6">
                  <div className="flex items-center gap-4">
                    <Clock className="w-5 h-5 text-copper-600" strokeWidth={1.2} />
                    <div>
                      <p className="text-[10px] tracking-[0.22em] uppercase text-ink-400 font-mono">Hours</p>
                      <p className="font-display text-lg text-ink-900">Mon – Fri · 9AM – 5PM PST</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form column */}
            <div className="lg:col-span-7">
              <div className="bg-cream-100 p-8 lg:p-12">
                <span className="text-eyebrow text-copper-600 font-mono">— Send us a message</span>
                <h2 className="font-display text-3xl text-ink-900 mt-4 mb-10">
                  We typically reply <span className="italic font-light">within 24h.</span>
                </h2>

                {submitted ? (
                  <div className="text-center py-14">
                    <div className="w-14 h-14 bg-copper-500 mx-auto mb-5 flex items-center justify-center">
                      <Send className="w-6 h-6 text-cream-50" />
                    </div>
                    <h3 className="font-display text-2xl text-ink-900 mb-2">Message received.</h3>
                    <p className="text-ink-500">Thanks for reaching out — we&rsquo;ll get back to you soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="text-[10px] tracking-[0.22em] uppercase text-ink-400 font-mono">Name</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full mt-2 bg-transparent border-0 border-b border-ink-300 py-2 text-ink-900 focus:outline-none focus:border-ink-900 transition-colors"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] tracking-[0.22em] uppercase text-ink-400 font-mono">Email</label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full mt-2 bg-transparent border-0 border-b border-ink-300 py-2 text-ink-900 focus:outline-none focus:border-ink-900 transition-colors"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.22em] uppercase text-ink-400 font-mono">Subject</label>
                      <input
                        type="text"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full mt-2 bg-transparent border-0 border-b border-ink-300 py-2 text-ink-900 focus:outline-none focus:border-ink-900 transition-colors"
                        placeholder="How can we help?"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.22em] uppercase text-ink-400 font-mono">Message</label>
                      <textarea
                        required
                        rows={5}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full mt-2 bg-transparent border-0 border-b border-ink-300 py-2 text-ink-900 focus:outline-none focus:border-ink-900 transition-colors resize-none"
                        placeholder="Tell us a bit more…"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit-button"
                      className="w-full py-4 bg-ink-900 text-cream-50 hover:bg-copper-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <span className="text-sm tracking-[0.15em] uppercase font-mono">
                        {submitting ? 'Sending…' : 'Send message'}
                      </span>
                      {!submitting && <ArrowRight className="w-4 h-4" />}
                    </button>
                    {errorMsg && (
                      <p className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3" data-testid="contact-error">
                        {errorMsg}
                      </p>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
